import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StudentLayout from "../../layouts/StudentLayout";
import Hero from "../../assets/demo-hero.png";
import { Arrow, FilledDropDown } from "../../components/Icons";
import Toast from "../../components/common/Toast";
import { useCheckEnrollmentStatusQuery } from "../../services/course";
import { useCreateDemoSessionRequestMutation } from "../../services/demosession/demoSessionApi";

/** HH:mm -> display e.g. "3:15 PM" */
function formatTime24To12(time24) {
  if (!time24) return "";
  const [hStr, mStr] = time24.split(":");
  let hour = parseInt(hStr, 10);
  const minutes = mStr || "00";
  const meridiem = hour >= 12 ? "PM" : "AM";
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${hour}:${minutes} ${meridiem}`;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatSelectedDate = (year, month, day) =>
  `${MONTH_NAMES[month]} ${day}, ${year}`;

/** selectedDate e.g. "May 11, 2026" + HH:mm -> ISO string */
function buildPreferredDateIso(selectedDate, time24h) {
  const parsed = Date.parse(selectedDate);
  if (Number.isNaN(parsed)) return new Date().toISOString();

  const base = new Date(parsed);
  const [hh, mm] = time24h.split(":").map((x) => parseInt(x, 10));
  base.setHours(hh || 0, mm || 0, 0, 0);
  return base.toISOString();
}

function getDemoRequestErrorMessage(err) {
  const d = err?.data;
  if (typeof d === "string") return d;
  if (d && typeof d.message === "string") return d.message;
  if (typeof err?.error === "string") return err.error;
  return "Could not book your demo. Please try again.";
}

const RequestCourseDemo = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDayNum, setSelectedDayNum] = useState(null);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedTime, setSelectedTime] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
  });

  const { data: enrollmentStatusResponse } = useCheckEnrollmentStatusQuery(
    courseId,
    { skip: !courseId },
  );
  const isAlreadyEnrolled =
    enrollmentStatusResponse?.data?.isEnrolled === true;

  const [createDemoSessionRequest, { isLoading: isCreatingDemo }] =
    useCreateDemoSessionRequestMutation();

  const showAlreadyEnrolledToast = () => {
    setToast({
      show: true,
      title: "Already enrolled",
      message:
        enrollmentStatusResponse?.message ||
        "You are already enrolled in this course.",
      variant: "error",
    });
  };

  const handleBookNow = () => {
    if (!selectedDate || !selectedTime) return;

    if (isAlreadyEnrolled) {
      showAlreadyEnrolledToast();
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmAttend = async () => {
    if (!courseId || !selectedDate || !selectedTime) return;

    if (isAlreadyEnrolled) {
      setShowConfirmModal(false);
      showAlreadyEnrolledToast();
      return;
    }

    const preferredTime = selectedTime;
    const preferredDate = buildPreferredDateIso(selectedDate, preferredTime);
    try {
      const result = await createDemoSessionRequest({
        courseId,
        preferredDate,
        preferredTime,
      }).unwrap();

      if (result && typeof result === "object" && result.success === false) {
        const msg =
          typeof result.message === "string"
            ? result.message
            : "Could not book your demo.";
        setToast({
          show: true,
          title: "Demo request not sent",
          message: msg,
          variant: "error",
        });
        return;
      }

      setShowConfirmModal(false);
      setShowSuccessModal(true);
    } catch (err) {
      setToast({
        show: true,
        title: "Demo request not sent",
        message: getDemoRequestErrorMessage(err),
        variant: "error",
      });
    }
  };

  const handleBookAnotherDemo = () => {
    setShowSuccessModal(false);
    setSelectedDate(null);
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDayNum(null);
    setSelectedTime("");
  };

  const canBook = Boolean(selectedDate && selectedTime);

  const handleCancel = () => {
    navigate(`/student/view-course/${courseId}`);
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Calendar starts on Monday (Mon=0 ... Sun=6)
  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const isPastDate = (year, month, day) => {
    const date = new Date(year, month, day);
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    return date < startOfToday;
  };

  const generateCalendarDays = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        isCurrentMonth: false,
        year: prevYear,
        month: prevMonth,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        year,
        month,
      });
    }

    const remainingDays = 42 - days.length;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        year: nextYear,
        month: nextMonth,
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays(viewYear, viewMonth);
  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const goToPreviousMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((year) => year - 1);
      return;
    }
    setViewMonth((month) => month - 1);
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((year) => year + 1);
      return;
    }
    setViewMonth((month) => month + 1);
  };

  const handleDateSelect = (dayObj) => {
    if (!dayObj?.isCurrentMonth) return;
    if (isPastDate(dayObj.year, dayObj.month, dayObj.day)) return;

    setSelectedYear(dayObj.year);
    setSelectedMonth(dayObj.month);
    setSelectedDayNum(dayObj.day);
    setSelectedDate(
      formatSelectedDate(dayObj.year, dayObj.month, dayObj.day),
    );
    setShowDatePicker(false);
  };
 

  return (
    <StudentLayout>
      <div className="grid grid-cols-1 lg:grid-cols-2 font-['Roboto']">
        {/* Left Side - Hero Section */}
        <div className="p-16">
          <button
            onClick={handleCancel}
            className="w-8 h-8 flex items-center justify-center"
          >
            <Arrow />
          </button>

          <div className="mt-[52px]">
            <h1 className="text-xl  text-black mb-5 leading-6">
              Ready to take the first step towards mastering your skills?
            </h1>
            <p className="text-xl text-[#4A4A68] leading-[23px]">
              Book a free demo lesson today and experience a personalized
              learning journey tailored just for you! Whether you're looking to
              explore new subjects or enhance your existing knowledge, our
              expert instructors will guide you every step of the way. Don't
              miss the chance to discover the perfect course that fits your
              goals. Start your learning adventure now!
            </p>
          </div>

          <div className="mt-36">
            <img src={Hero} alt="Demo Hero" className="w-full h-full" />
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div className="px-20  pt-20 pb-16 shadow-[0_0_3.7px_4px_#00000040]">
          <div>
            <h2 className="text-[32px] text-center font-medium leading-[38px] text-black mb-2">
              Request a Course Demo
            </h2>
            <p className="border-b pb-6 border-[#00000080] text-lg leading-6 text-center text-black mt-3">
              Tell us about your learning interests and we'll show you exactly
              what to expect
            </p>
          </div>

          {/* Select Date */}
          <div className=" mt-14">
            <label className="block text-[22px] leading-[26px] font-medium text-black mb-4">
              Select Date
            </label>
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`w-full px-5 py-[13px] border rounded-lg text-[#938A96] text-xl leading-6 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors ${
                  showDatePicker ? "border-[#FF6636]" : "border-[#00000080]"
                }`}
              >
                <span className={selectedDate ? "text-black" : ""}>
                  {selectedDate ?? "Select Date"}
                </span>
                <FilledDropDown color="#000" />
              </button>

              {/* Calendar Picker */}
              {showDatePicker && (
                <div className="absolute top-full left-0 mt-3 bg-white border border-gray-300 rounded-lg shadow-lg p-6 z-50 w-full max-w-sm">
                  <div className="flex items-center justify-between mb-6">
                    <button
                      type="button"
                      onClick={goToPreviousMonth}
                      className="text-gray-400 hover:text-gray-600"
                      aria-label="Previous month"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15 18L9 12L15 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <h3 className="text-xl font-bold text-black">
                      {MONTH_NAMES[viewMonth]} {viewYear}
                    </h3>
                    <button
                      type="button"
                      onClick={goToNextMonth}
                      className="text-gray-400 hover:text-gray-600"
                      aria-label="Next month"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 18L15 12L9 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Week Days Header */}
                  <div className="grid grid-cols-7 gap-2 mb-4">
                    {weekDays.map((day) => (
                      <div
                        key={day}
                        className="text-center text-sm font-medium text-gray-500"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-2">
                    {calendarDays.map((dayObj, idx) => {
                      const isSelected =
                        selectedDayNum != null &&
                        selectedYear === dayObj.year &&
                        selectedMonth === dayObj.month &&
                        dayObj.day === selectedDayNum &&
                        dayObj.isCurrentMonth;
                      const isDisabled =
                        !dayObj.isCurrentMonth ||
                        isPastDate(dayObj.year, dayObj.month, dayObj.day);

                      return (
                        <button
                          key={`${dayObj.year}-${dayObj.month}-${dayObj.day}-${idx}`}
                          type="button"
                          onClick={() => handleDateSelect(dayObj)}
                          disabled={isDisabled}
                          className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
                            isSelected
                              ? "bg-[#FF6636] text-white"
                              : dayObj.isCurrentMonth && !isDisabled
                                ? "text-black hover:bg-gray-100 cursor-pointer"
                                : "text-gray-300 cursor-not-allowed"
                          }`}
                        >
                          {dayObj.day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Selected Date Display */}
          <div className=" mt-8">
            <label className="block text-[22px] leading-[26px] font-medium text-black mb-3">
              Selected Date
            </label>
            <div
              className={`inline-block px-8 py-3 rounded-lg text-xl ${
                selectedDate
                  ? "bg-[#FF6636] text-white"
                  : "bg-gray-100 text-[#938A96] border border-[#00000040]"
              }`}
            >
              {selectedDate ?? "No date selected yet"}
            </div>
          </div>

          {/* Select Time */}
          <div className="mt-12">
            <label
              htmlFor="demo-time"
              className="mb-3 block text-[22px] font-medium leading-[26px] text-black"
            >
              Select a Time
            </label>
            <input
              id="demo-time"
              type="time"
              value={selectedTime}
              onChange={(event) => setSelectedTime(event.target.value)}
              className="w-full rounded-lg border border-[#00000080] bg-white px-5 py-[13px] text-xl leading-6 text-black outline-none focus:border-[#FF6636]"
            />
          </div>

          {/* Selected Time Display */}
          <div className="mt-8">
            <label className="mb-3 block text-[22px] font-medium leading-[26px] text-black">
              Selected Time
            </label>
            <div
              className={`inline-block rounded-lg px-8 py-3 text-xl ${
                selectedTime
                  ? "bg-[#FF6636] text-white"
                  : "border border-[#00000040] bg-gray-100 text-[#938A96]"
              }`}
            >
              {selectedTime
                ? formatTime24To12(selectedTime)
                : "No time selected yet"}
            </div>
          </div>

          <div className="mb-10 mt-14 flex max-w-[531px] gap-3">
            <button
              type="button"
              onClick={handleBookNow}
              disabled={!canBook}
              className="flex-1 bg-[#FF6636] text-white text-xl font-medium py-3 rounded-lg hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#FF6636]"
            >
              Book Now
            </button>
            <button
              onClick={handleCancel}
              className="flex-1 bg-white text-xl leading-6 text-gray-800 font-medium py-3 rounded-lg border border-[#00000080] hover:border-gray-400 transition"
            >
              Cancel
            </button>
          </div>

          <p className="text-xl leading-[26px] text-[#4A4A68] ">
            Your assigned <span className="text-black">STEAM Minds</span>{" "}
            Instructor will be ready to guide you at the scheduled class time.
            Please make sure you are available when booking, as missing the
            class would result in valuable time being wasted!
          </p>
        </div>
      </div>

      {showConfirmModal ? (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/50 p-4 sm:items-center sm:py-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-confirm-title"
        >
          <div className="relative my-auto w-full max-w-[916px] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl bg-white px-5 py-8 sm:rounded-[32px] sm:px-10 sm:py-10 md:rounded-[50px] md:px-16 md:py-14">
            <button
              type="button"
              onClick={() => setShowConfirmModal(false)}
              disabled={isCreatingDemo}
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 disabled:pointer-events-none disabled:opacity-40 sm:right-5 sm:top-5 sm:h-10 sm:w-10"
              aria-label="Close"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <h2
              id="demo-confirm-title"
              className="mx-auto mt-10 max-w-[711px] text-center text-xl font-medium leading-7 text-black sm:mt-14 sm:text-2xl sm:leading-8 md:mt-16 md:text-[32px] md:leading-[38px]"
            >
              Are you sure that you are available at the selected date &amp; time
              to join the free lesson?
            </h2>

            <p className="mx-auto mt-5 max-w-[690px] text-center text-base leading-6 text-black sm:mt-6 sm:text-lg sm:leading-[23px] md:text-xl">
              Your Instructor will wait for you in the class. If you don&apos;t
              join, their precious time will be wasted. Confirm only if you are
              sure that you will join.
            </p>

            <div className="mx-auto mt-8 flex max-w-[711px] flex-col gap-3 sm:mt-12 sm:gap-4 md:mt-16 md:gap-5">
              <button
                type="button"
                onClick={handleConfirmAttend}
                disabled={isCreatingDemo}
                className="w-full rounded-lg bg-[#FF6636] py-3 text-center text-lg font-medium text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-[#FF6636] sm:py-4 sm:text-xl md:text-2xl"
              >
                {isCreatingDemo ? "Booking…" : "Yes, I'll Attend"}
              </button>
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                disabled={isCreatingDemo}
                className="w-full rounded-lg border border-[#E8D4D4] bg-[#FFF5F5] py-3 text-center text-base font-medium text-black transition hover:bg-[#FF66361A] disabled:cursor-not-allowed disabled:opacity-50 sm:py-4 sm:text-lg md:text-2xl"
              >
                No, I want to Change Date/ Time
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showSuccessModal ? (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/50 p-4 sm:items-center sm:py-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-success-title"
        >
          <div className="relative my-auto w-full max-w-[762px] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl sm:rounded-3xl">
            <div className="flex items-center justify-end border-b border-gray-200 px-4 py-3 sm:px-8 sm:py-4">
              <button
                type="button"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(`/student/view-course/${courseId}`);
                }}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 sm:h-10 sm:w-10"
                aria-label="Close"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className="px-5 pb-8 pt-6 text-center sm:px-10 sm:pb-12 sm:pt-8 md:px-12 md:pt-10">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-black sm:h-16 sm:w-16">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="sm:h-8 sm:w-8"
                  aria-hidden
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <h2
                id="demo-success-title"
                className="mt-5 text-xl font-bold text-black sm:mt-6 sm:text-2xl md:text-3xl"
              >
                Demo Lesson Booked!
              </h2>

              <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-[#4A4A68] sm:mt-4 sm:text-base md:text-lg">
                Your demo lesson has been successfully scheduled. We&apos;ll send
                you a confirmation email with all the details shortly.
              </p>

              <button
                type="button"
                onClick={handleBookAnotherDemo}
                className="mx-auto mt-8 w-full max-w-md rounded-xl bg-[#FFE8E0] py-3 text-center text-base font-bold text-black transition hover:bg-[#FFD9CC] sm:mt-10 sm:py-4 sm:text-lg md:text-xl"
              >
                Book Another Demo
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast.show ? (
        <Toast
          variant="error"
          title={toast.title}
          message={toast.message}
          duration={9000}
          onClose={() => setToast((t) => ({ ...t, show: false }))}
        />
      ) : null}
    </StudentLayout>
  );
};

export default RequestCourseDemo;
